import { useEffect, useMemo, useState } from "react";
import { Users } from "lucide-react";
import { getAuthToken } from "../../utils/authSession";
import { getPlans, Plan } from "../../services/planService";
import {
    buildClassJoinUrl,
    getClasses,
    LiveClassRecord,
    requestClassJoin,
} from "../../services/classService";
import {
    formatClassDate,
    formatClassDateTimeWithZone,
    formatClassTimeOnly,
    getClassTimingStatus,
    isClassPastByEnd,
} from "../../utils/classTime";
import { showErrorToast } from "../../utils/toaster";

const PAGE_SIZE = 9;

interface EndedClassModalState {
    className: string;
    endedAtLabel: string;
}

function hasRecordedClassLink(classItem: LiveClassRecord): boolean {
    return Boolean(classItem.recorded_class_link?.trim());
}

function planNamesForClass(classItem: LiveClassRecord, planMap: Map<number, string>): string {
    if (!classItem.plan_ids?.length) {
        return "All plans";
    }

    return classItem.plan_ids
        .map((planId) => planMap.get(planId) || `Plan ${planId}`)
        .join(", ");
}

function PaginatedSection({
    title,
    items,
    planMap,
    onJoin,
    nowMs,
}: {
    title: string;
    items: LiveClassRecord[];
    planMap: Map<number, string>;
    onJoin: (classItem: LiveClassRecord) => void;
    nowMs: number;
}) {
    const [page, setPage] = useState(0);
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const visibleItems = items.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    useEffect(() => {
        setPage(0);
    }, [items.length]);

    if (!items.length) {
        return null;
    }

    return (
        <section className="space-y-4">
            <h3 className="text-lg md:text-xl font-bold text-[#113A28]">{title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {visibleItems.map((classItem) => {
                    const status = getClassTimingStatus(classItem.class_date, classItem.end_date, nowMs);
                    const statusColor =
                        status === "live"
                            ? "bg-emerald-500"
                            : status === "upcoming"
                              ? "bg-amber-400"
                              : "bg-red-500";

                    return (
                        <article
                            key={classItem.id}
                            className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start gap-3">
                                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#113A28]/10 text-[#113A28]">
                                    <Users size={22} strokeWidth={2} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`h-2.5 w-2.5 rounded-full ${statusColor}`} aria-hidden />
                                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                            {status === "live" ? "Live now" : status === "upcoming" ? "Upcoming" : "Past"}
                                        </p>
                                    </div>
                                    <h4 className="text-base font-extrabold text-[#113A28] leading-snug">{classItem.name}</h4>
                                    <p className="text-xs font-medium text-gray-500 mt-1">{planNamesForClass(classItem, planMap)}</p>
                                </div>
                            </div>

                            <div className="mt-4 space-y-1 text-sm text-gray-600">
                                <p>
                                    <span className="font-bold text-[#113A28]">Date:</span>{" "}
                                    {formatClassDate(classItem.class_date)}
                                </p>
                                <p>
                                    <span className="font-bold text-[#113A28]">Starts:</span>{" "}
                                    {formatClassTimeOnly(classItem.class_date)}
                                </p>
                                <p>
                                    <span className="font-bold text-[#113A28]">Ends:</span>{" "}
                                    {formatClassTimeOnly(classItem.end_date)}
                                </p>
                                <p className="text-xs text-gray-500">{formatClassDateTimeWithZone(classItem.class_date)}</p>
                            </div>

                            <div className="mt-5">
                                {status === "live" && (
                                    <button
                                        type="button"
                                        onClick={() => onJoin(classItem)}
                                        className="w-full rounded-xl bg-[#4CE470] px-4 py-3 text-sm font-bold text-[#113A28] hover:bg-[#3bc45d] transition-colors"
                                    >
                                        Join Live Class
                                    </button>
                                )}
                                {status === "upcoming" && (
                                    <p className="text-sm font-semibold text-[#113A28] bg-gray-50 rounded-xl px-4 py-3 text-center">
                                        Starts {formatClassDateTimeWithZone(classItem.class_date)}
                                    </p>
                                )}
                                {status === "past" && hasRecordedClassLink(classItem) && (
                                    <button
                                        type="button"
                                        onClick={() =>
                                            window.open(
                                                classItem.recorded_class_link!.trim(),
                                                "_blank",
                                                "noopener,noreferrer",
                                            )
                                        }
                                        className="w-full rounded-xl bg-[#113A28] px-4 py-3 text-sm font-bold text-white hover:bg-[#1a573c] transition-colors"
                                    >
                                        View Class
                                    </button>
                                )}
                                {status === "past" && !hasRecordedClassLink(classItem) && (
                                    <p className="text-sm font-medium text-gray-500 bg-gray-50 rounded-xl px-4 py-3 text-center">
                                        Ended {formatClassDateTimeWithZone(classItem.end_date)}
                                    </p>
                                )}
                            </div>
                        </article>
                    );
                })}
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-2">
                    <button
                        type="button"
                        disabled={safePage === 0}
                        onClick={() => setPage((current) => Math.max(0, current - 1))}
                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-bold text-[#113A28] disabled:opacity-40"
                    >
                        Previous
                    </button>
                    <span className="text-sm font-semibold text-gray-500">
                        Page {safePage + 1} of {totalPages}
                    </span>
                    <button
                        type="button"
                        disabled={safePage >= totalPages - 1}
                        onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-bold text-[#113A28] disabled:opacity-40"
                    >
                        Next
                    </button>
                </div>
            )}
        </section>
    );
}

const LearnerLiveClassesPanel = () => {
    const [classes, setClasses] = useState<LiveClassRecord[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [endedModal, setEndedModal] = useState<EndedClassModalState | null>(null);

    useEffect(() => {
        const intervalId = window.setInterval(() => setNowMs(Date.now()), 30000);
        return () => window.clearInterval(intervalId);
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            const token = getAuthToken();
            if (!token) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setLoadError("");
                const [classesResponse, plansResponse] = await Promise.all([
                    getClasses(token),
                    getPlans(),
                ]);

                if (!isMounted) {
                    return;
                }

                setClasses(classesResponse.data || []);
                setPlans(plansResponse.data || []);
            } catch (error) {
                if (isMounted) {
                    setLoadError(error instanceof Error ? error.message : "Unable to load live classes.");
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, []);

    const planMap = useMemo(() => new Map(plans.map((plan) => [plan.id, plan.name])), [plans]);

    const { liveAndUpcoming, past } = useMemo(() => {
        const liveAndUpcomingItems: LiveClassRecord[] = [];
        const pastItems: LiveClassRecord[] = [];

        classes.forEach((classItem) => {
            if (isClassPastByEnd(classItem.end_date, nowMs)) {
                pastItems.push(classItem);
            } else {
                liveAndUpcomingItems.push(classItem);
            }
        });

        const statusOrder = { live: 0, upcoming: 1, past: 2 };
        liveAndUpcomingItems.sort((left, right) => {
            const leftStatus = getClassTimingStatus(left.class_date, left.end_date, nowMs);
            const rightStatus = getClassTimingStatus(right.class_date, right.end_date, nowMs);
            const statusDiff = statusOrder[leftStatus] - statusOrder[rightStatus];
            if (statusDiff !== 0) {
                return statusDiff;
            }

            return new Date(left.class_date).getTime() - new Date(right.class_date).getTime();
        });
        pastItems.sort(
            (left, right) => new Date(right.end_date).getTime() - new Date(left.end_date).getTime(),
        );

        return { liveAndUpcoming: liveAndUpcomingItems, past: pastItems };
    }, [classes, nowMs]);

    const handleJoin = async (classItem: LiveClassRecord) => {
        if (isClassPastByEnd(classItem.end_date, Date.now())) {
            setEndedModal({
                className: classItem.name,
                endedAtLabel: formatClassDateTimeWithZone(classItem.end_date),
            });
            setNowMs(Date.now());
            return;
        }

        const token = getAuthToken();
        if (!token) {
            showErrorToast("Your session has expired. Please login again.");
            return;
        }

        try {
            const response = await requestClassJoin(token, classItem.id);
            const joinUrl = buildClassJoinUrl(response.data.join_path);
            window.open(joinUrl, "_blank", "noopener,noreferrer");
        } catch (error) {
            const message = error instanceof Error ? error.message : "Unable to join this class.";
            if (message.toLowerCase().includes("ended")) {
                setEndedModal({
                    className: classItem.name,
                    endedAtLabel: formatClassDateTimeWithZone(classItem.end_date),
                });
                setNowMs(Date.now());
                return;
            }

            showErrorToast(message);
        }
    };

    if (isLoading) {
        return (
            <div className="py-16 text-center text-sm font-semibold text-gray-500">
                Loading live classes...
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="py-10 text-center text-sm font-semibold text-red-600">
                {loadError}
            </div>
        );
    }

    if (!liveAndUpcoming.length && !past.length) {
        return (
            <div className="py-16 text-center">
                <p className="text-lg font-bold text-[#113A28]">No live classes yet</p>
                <p className="mt-2 text-sm text-gray-500">Check back soon for upcoming sessions on your plan.</p>
            </div>
        );
    }

    return (
        <div className="space-y-10">
            <PaginatedSection
                title="Live & Upcoming"
                items={liveAndUpcoming}
                planMap={planMap}
                onJoin={handleJoin}
                nowMs={nowMs}
            />
            <PaginatedSection
                title="Past Classes"
                items={past}
                planMap={planMap}
                onJoin={handleJoin}
                nowMs={nowMs}
            />

            {endedModal && (
                <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#113A28]/50 px-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl text-center">
                        <h3 className="text-xl font-extrabold text-[#113A28]">Class has ended</h3>
                        <p className="mt-3 text-sm text-gray-600">
                            <span className="font-bold text-[#113A28]">{endedModal.className}</span> ended at{" "}
                            {endedModal.endedAtLabel}.
                        </p>
                        <button
                            type="button"
                            onClick={() => setEndedModal(null)}
                            className="mt-6 w-full rounded-xl bg-[#113A28] px-4 py-3 text-sm font-bold text-white hover:bg-[#1a573c]"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LearnerLiveClassesPanel;
